import "@/App.css";
import "./i18n";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";

import { AuthProvider } from "./contexts/AuthContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { ScrollToTop } from "./components/ScrollToTop";
import { CursorGlowController } from "./components/anim/CursorGlowController";
import SEOHead from "./components/SEOHead";
import GTMLoader from "./components/GTMLoader";
import { TrafficBeacon } from "./components/TrafficBeacon";
import CookieBanner from "./components/CookieBanner";

// Public marketing
import Home from "./pages/Home";
import NetworkOverview from "./pages/NetworkOverview";
import NetworkLedger from "./pages/NetworkLedger";
import NetworkOnchainID from "./pages/NetworkOnchainID";
import NetworkERC3643 from "./pages/NetworkERC3643";
import SolutionsInstitutions from "./pages/SolutionsInstitutions";
import SolutionsBuilders from "./pages/SolutionsBuilders";
import SolutionsAppStore from "./pages/SolutionsAppStore";
import Ecosystem from "./pages/Ecosystem";
import Learn from "./pages/Learn";
import LearnGuide from "./pages/LearnGuide";
import About from "./pages/About";
import Contact from "./pages/Contact";
import Articles from "./pages/Articles";
import ArticleDetail from "./pages/ArticleDetail";
import Press from "./pages/Press";
import DocsHome from "./pages/DocsHome";
import DocsPage from "./pages/DocsPage";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import Disclosures from "./pages/Disclosures";
import DigitalAssetWeek from "./pages/DigitalAssetWeek";
import { DocsLayout } from "./components/docs/DocsLayout";

// Admin
import AdminLogin from "./admin/AdminLogin";
import AuthCallback from "./admin/AuthCallback";
import AdminLayout from "./admin/AdminLayout";
import AdminDashboard from "./admin/pages/AdminDashboard";
import AdminArticles from "./admin/pages/AdminArticles";
import AdminArticleEditor from "./admin/pages/AdminArticleEditor";
import AdminMedia from "./admin/pages/AdminMedia";
import AdminContacts from "./admin/pages/AdminContacts";
import AdminAdmins from "./admin/pages/AdminAdmins";
import AdminSitePages from "./admin/pages/AdminSitePages";
import AdminMembers from "./admin/pages/AdminMembers";
import AdminDocs from "./admin/pages/AdminDocs";
import AdminDocEditor from "./admin/pages/AdminDocEditor";
import AdminSEO from "./admin/pages/AdminSEO";

function AppRouter() {
  const location = useLocation();
  // CRITICAL: Detect session_id during render (not in useEffect) to handle race conditions.
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <>
      <ScrollToTop />
      <CursorGlowController />
      <SEOHead />
      <GTMLoader />
      <TrafficBeacon />
      {/* Unlisted event pages carry no cookie banner (it collided with the card carousel) */}
      {!location.pathname.startsWith("/events/") && <CookieBanner />}
      <Routes>
        {/* Public marketing site */}
        <Route path="/" element={<Home />} />

        <Route path="/network" element={<NetworkOverview />} />
        <Route path="/network/ledger" element={<NetworkLedger />} />
        <Route path="/network/erc-3643" element={<NetworkERC3643 />} />
        {/* Identity page paused: kept in the codebase, hidden from the live site
            until the copy and the audit link are cleared. Re-enable by restoring
            the route below, plus the Navbar, Footer, sitemap and SEO entries. */}
        <Route path="/network/identity" element={<Navigate to="/network" replace />} />
        <Route path="/network/onchainid" element={<Navigate to="/network" replace />} />

        <Route path="/solutions/institutions" element={<SolutionsInstitutions />} />
        <Route path="/solutions/builders" element={<SolutionsBuilders />} />
        <Route path="/solutions/app-store" element={<SolutionsAppStore />} />

        <Route path="/ecosystem" element={<Ecosystem />} />
        <Route path="/learn" element={<Learn />} />
        <Route path="/learn/:guideSlug" element={<LearnGuide />} />
        <Route path="/about" element={<About />} />
        <Route path="/articles" element={<Articles />} />
        <Route path="/articles/:slug" element={<ArticleDetail />} />
        <Route path="/contact" element={<Contact />} />

        {/* Footer-only (no nav entry) */}
        <Route path="/press" element={<Press />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/disclosures" element={<Disclosures />} />

        {/* Unlisted event page (QR only, not in nav or sitemap) */}
        <Route path="/events/digital-asset-week" element={<DigitalAssetWeek />} />

        {/* Docs (public) */}
        <Route path="/docs" element={<DocsLayout />}>
          <Route index element={<DocsHome />} />
          <Route path=":sectionSlug/:pageSlug" element={<DocsPage />} />
        </Route>

        {/* 301-style redirects from old URLs */}
        <Route path="/why-t-rex" element={<Navigate to="/network" replace />} />
        <Route path="/t-rex-ledger" element={<Navigate to="/network/ledger" replace />} />
        <Route path="/erc-3643" element={<Navigate to="/network/erc-3643" replace />} />
        <Route path="/onchainid" element={<Navigate to="/network" replace />} />
        <Route path="/identity" element={<Navigate to="/network" replace />} />
        <Route path="/app-store" element={<Navigate to="/solutions/app-store" replace />} />
        <Route path="/token" element={<Navigate to="/" replace />} />
        <Route path="/articles/apex-group-100b-target" element={<Navigate to="/articles/apex-group-to-enable-global-distribution-of-tokenized-funds-using-the-t-rex-ledger-as-its-multi-chain-orchestration-layer" replace />} />

        {/* Admin */}
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
          <Route index element={<AdminDashboard />} />
          <Route path="articles" element={<AdminArticles />} />
          <Route path="articles/new" element={<AdminArticleEditor />} />
          <Route path="articles/:id" element={<AdminArticleEditor />} />
          <Route path="media" element={<AdminMedia />} />
          <Route path="members" element={<AdminMembers />} />
          <Route path="contacts" element={<AdminContacts />} />
          <Route path="admins" element={<AdminAdmins />} />
          <Route path="pages" element={<AdminSitePages />} />
          <Route path="docs" element={<AdminDocs />} />
          <Route path="docs/new" element={<AdminDocEditor />} />
          <Route path="docs/:id" element={<AdminDocEditor />} />
          <Route path="seo" element={<AdminSEO />} />
          <Route path="seo/:page" element={<AdminSEO />} />
        </Route>
      </Routes>
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <AuthProvider>
          <div className="App">
            <AppRouter />
          </div>
        </AuthProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
